import type { MemoryResp } from "@/types/memory.ts";

// Shared between web and native (imported via the `@/shared` symlink) so both
// apps bucket and order memories identically. Pure logic only — its sole
// dependency is the shared MemoryResp type.

/** Get the start of the week (Sunday) for a given date */
const getStartOfWeek = (date: Date): Date => {
  const d = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  d.setDate(d.getDate() - d.getDay());
  return d;
};

/** Get the relative date bucket label for a date string */
export const getDateBucket = (dateStr: string, now: Date): string => {
  // Date-only strings ("YYYY-MM-DD") parse as UTC midnight, which reads as the
  // previous day for users west of UTC once compared against local calendar
  // days below. Parse those as local midnight; pass full timestamps through.
  const dateOnlyMatch = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dateStr);
  const date = dateOnlyMatch
    ? new Date(Number(dateOnlyMatch[1]), Number(dateOnlyMatch[2]) - 1, Number(dateOnlyMatch[3]))
    : new Date(dateStr);

  // Handle invalid dates
  if (Number.isNaN(date.getTime())) {
    return "Unknown";
  }

  // Reset times to midnight for day comparison
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const dateDay = new Date(date.getFullYear(), date.getMonth(), date.getDate());

  const diffMs = today.getTime() - dateDay.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  // Today / Yesterday
  if (diffDays === 0) return "Today";
  if (diffDays === 1) return "Yesterday";

  // This Week / Last Week (calendar weeks)
  const startOfThisWeek = getStartOfWeek(today);
  const startOfLastWeek = new Date(startOfThisWeek);
  startOfLastWeek.setDate(startOfLastWeek.getDate() - 7);

  if (dateDay >= startOfThisWeek) return "This Week";
  if (dateDay >= startOfLastWeek) return "Last Week";

  // This Month / Last Month
  const startOfThisMonth = new Date(now.getFullYear(), now.getMonth(), 1);
  const startOfLastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);

  if (dateDay >= startOfThisMonth) return "This Month";
  if (dateDay >= startOfLastMonth) return "Last Month";

  // Older: show month and year (e.g., "January 2025")
  return date.toLocaleDateString("en-US", { month: "long", year: "numeric" });
};

/**
 * Group memories by date bucket and sort each group by quality score (highest
 * first). `now` is the reference point the relative buckets ("Today", "This
 * Week") are computed against; pass a fixed one to keep the labels stable for
 * as long as a caller holds onto them, since a screen keyed by a relative label
 * would otherwise lose its memories when the clock crosses midnight.
 */
export const groupMemoriesByDate = (
  memories: MemoryResp[],
  now: Date = new Date(),
): Array<[string, MemoryResp[]]> => {
  const groups = new Map<string, MemoryResp[]>();
  const bucketDates = new Map<string, Date>(); // Track a representative date for each bucket

  for (const memory of memories) {
    const bucket = getDateBucket(memory.date, now);
    if (!groups.has(bucket)) {
      groups.set(bucket, []);
      bucketDates.set(bucket, new Date(memory.date));
    }
    groups.get(bucket)?.push(memory);
  }

  // Sort memories within each bucket by quality score (highest first)
  for (const [, bucketMemories] of groups) {
    bucketMemories.sort((a, b) => (b.qualityScore ?? 0) - (a.qualityScore ?? 0));
  }

  // Sort buckets: fixed buckets first in order, then month/year buckets by date (newest first)
  const fixedBuckets = ["Today", "Yesterday", "This Week", "Last Week", "This Month", "Last Month"];
  const sortedBuckets = Array.from(groups.keys()).sort((a, b) => {
    const aIndex = fixedBuckets.indexOf(a);
    const bIndex = fixedBuckets.indexOf(b);

    // Both are fixed buckets - sort by their fixed order
    if (aIndex !== -1 && bIndex !== -1) return aIndex - bIndex;
    // Only a is fixed - it comes first
    if (aIndex !== -1) return -1;
    // Only b is fixed - it comes first
    if (bIndex !== -1) return 1;
    // Both are month/year buckets - sort by representative date (newest first)
    const aDate = bucketDates.get(a)?.getTime() ?? 0;
    const bDate = bucketDates.get(b)?.getTime() ?? 0;
    return bDate - aDate;
  });

  return sortedBuckets.map((bucket) => [bucket, groups.get(bucket) ?? []]);
};
